import { fromJS, List, Map } from 'immutable';
import Alarm, { arcForwardStateOf } from '../../utils/models/Alarm';
import {
	CLOSE_ALARMS,
	OPEN_ALARMS,
	SAFETY_REGIONS_CHANGED,
	SET_ALARM_SOUND_ENABLED,
	SET_ALERT_SOUND_ENABLED,
	SET_ASS_REQ_SOUND_ENABLED, SET_DETAILED_ALARM, SET_RECORDING, TOGGLE_ARC_ALARMS,
	V4_ALARMS_CLOSED,
	V4_ALARMS_LOADED,
	V4_ALARM_FORWARDING_CHANGED,
	FETCH_V4_CLOSED_ALARMS,
	V4_CLOSED_ALARMS_LOADED,
	V4_CLOSED_ALARMS_RESET,
	SET_ALARM_LOCATION_VIEW,
	LATEST_ALARM_POSITION_LOADED,
	LATEST_ALARM_POSITION_FAILED,
	WS_DATA_ALARM,
	WS_DATA_ALARMS,
	WS_REQUEST_RELOAD_DATA
} from '../constants/alarm.constants';
import { AUTH_SUCCESS } from '../../containers/LoginPage/constants';

export const initialState = fromJS({
	fetched: false,
	data: {},
	v4Alarms: List(),
	// Closed V4 alarms are fetched lazily (on expand) from a separate endpoint and
	// kept apart from `v4Alarms` so open-list refreshes don't clobber them.
	v4ClosedAlarms: List(),
	v4ClosedAlarmsFetching: false,
	// Gates background refreshes — only re-fetch while the section is loaded/viewed.
	v4ClosedAlarmsFetched: false,
	safetyRegions: null,
	alarmSoundEnabled: localStorage.getItem('playAlarmSound') || false,
	alertSoundEnabled: localStorage.getItem('playAlertSound') || false,
	assReqSoundEnabled: localStorage.getItem('playAssReqSound') || false,
	selectedAlarms: [],
	detailedAlarm: null,
	loadingDetailedAlarm: false,
	recording: null,
	// {alarmId, view}
	alarmLocationView: null,
	// {alarmId, position, failed}: read when the alarm's device isn't listed.
	latestAlarmPosition: null,
	showARCAlarms: (localStorage.getItem('showARCAlarms') || 'false') === 'true',
	// alarmId -> last arc-forwarding status seen live on the websocket. Lets a
	// list refetch re-apply / not clobber a status the read model hasn't caught
	// up to yet (fetch-vs-subscription race).
	v4ForwardingStatus: Map(),
	// alarmId -> closed copy, kept until the closed list shows it.
	v4ClosedLocally: Map()
});

// forwarding = in progress (1), success/failed = terminal (2), none = 0.
const forwardingRank = (s) => (s === 'success' || s === 'failed' ? 2 : s === 'forwarding' ? 1 : 0);

const isDetailedAlarm = (state, alarmId) => {
	const detailed = state.get('detailedAlarm');
	return !!detailed && alarmId != null && String(detailed.id) === String(alarmId);
};

const keptLatestPosition = (state, alarmId) => {
	const kept = state.get('latestAlarmPosition');
	return kept && kept.alarmId === String(alarmId) ? kept : null;
};

const samePosition = (a, b) =>
	(!a && !b) || (!!a && !!b && a.id === b.id && a.serverTime === b.serverTime);

function reducer(state: Map = initialState, action) {
	let alarms = state.get('data');
	switch(action.type) {
		case AUTH_SUCCESS:
			return initialState;
		case WS_REQUEST_RELOAD_DATA:
			return state.set('data', fromJS({}));
		case WS_DATA_ALARM:
			return state
				.set('data', new Map({
					...alarms.toObject(),
					[+action.data.id]: new Alarm(action.data)
				}));
		case WS_DATA_ALARMS:
			const newAlarms = Map(action.data.map(d => new Alarm(d)).map(d => [d.id, d]));
			return state
				.set('data', new Map({
					...alarms.toObject(),
					...newAlarms.toObject()
				}))
				.set('fetched', true);
		case SAFETY_REGIONS_CHANGED:
			return state.set('safetyRegions', action.data);
		case SET_ALARM_SOUND_ENABLED:
			if(action.isEnabled) localStorage.setItem("playAlarmSound", true);
			else localStorage.removeItem("playAlarmSound");
			return state.set('alarmSoundEnabled', !!action.isEnabled);
		case SET_ALERT_SOUND_ENABLED:
			if(action.isEnabled) localStorage.setItem("playAlertSound", true);
			else localStorage.removeItem("playAlertSound");
			return state.set('alertSoundEnabled', !!action.isEnabled);
		case SET_ASS_REQ_SOUND_ENABLED:
			if(action.isEnabled) localStorage.setItem("playAssReqSound", true);
			else localStorage.removeItem("playAssReqSound");
			return state.set('assReqSoundEnabled', !!action.isEnabled);
		case SET_DETAILED_ALARM: {
			// Every V4 refetch re-sets the same alarm; keep its recording playing.
			const previous = state.get('detailedAlarm');
			const sameAlarm = !!previous && !!action.alarm && String(previous.id) === String(action.alarm.id);
			return state
				.set('recording', sameAlarm ? state.get('recording') : null)
				.set('alarmLocationView', sameAlarm ? state.get('alarmLocationView') : null)
				.set('latestAlarmPosition', sameAlarm ? state.get('latestAlarmPosition') : null)
				.set('detailedAlarm', action.alarm)
				.set('loadingDetailedAlarm', false);
		}
		case TOGGLE_ARC_ALARMS:
			const enabled = !state.get('showARCAlarms');
			localStorage.setItem('showARCAlarms', enabled ? 'true' : 'false');
			return state.set('showARCAlarms', enabled);
		case OPEN_ALARMS:
		case CLOSE_ALARMS:
			if(state.get('detailedAlarm') && action.alarms.find(a => a.id === state.get('detailedAlarm').id)) {
				return state.set('loadingDetailedAlarm', true);
			}
			return state;
		case SET_RECORDING:
			return state.set('recording', action.recording);
		case SET_ALARM_LOCATION_VIEW:
			return state.set('alarmLocationView', action.alarmId != null
				? {alarmId: String(action.alarmId), view: action.view}
				: null);
		case LATEST_ALARM_POSITION_LOADED: {
			if (!isDetailedAlarm(state, action.alarmId)) return state;
			const kept = keptLatestPosition(state, action.alarmId);
			const next = action.position;
			// Like the device list: never an older fix, nor none over one.
			if (kept?.position && (!next || next.serverTime < kept.position.serverTime)) return state;
			if (kept && !kept.failed && samePosition(kept.position, next)) return state;
			return state.set('latestAlarmPosition', {alarmId: String(action.alarmId), position: next || null, failed: false});
		}
		case LATEST_ALARM_POSITION_FAILED: {
			if (!isDetailedAlarm(state, action.alarmId)) return state;
			if (keptLatestPosition(state, action.alarmId)?.position) return state;
			return state.set('latestAlarmPosition', {alarmId: String(action.alarmId), position: null, failed: true});
		}
		case V4_ALARMS_LOADED: {
			const overrides = state.get('v4ForwardingStatus') || Map();
			let nextOverrides = overrides;
			const list = List(action.alarms.map(d => {
				const live = overrides.get(d.id);
				if (live === 'success' || live === 'failed') {
					// Terminal live status is sticky — no later/stale refetch can revert it.
					d = { ...d, arcForward: { ...(d.arcForward || {}), status: live } };
				} else if (live) {
					// live === 'forwarding': keep spinner over a null snapshot, else let
					// a terminal backend value take over and drop the override.
					if (forwardingRank(live) > forwardingRank(arcForwardStateOf(d.arcForward))) {
						d = { ...d, arcForward: { ...(d.arcForward || {}), status: live } };
					} else {
						nextOverrides = nextOverrides.delete(d.id);
					}
				}
				return new Alarm(d);
			}));
			return state.set('v4Alarms', list).set('v4ForwardingStatus', nextOverrides);
		}
		// Optimistic close: move the alarms across the lists without waiting on a
		// refresh. `mergedAlarms` lets the closed copy win, so a refresh that hasn't
		// caught up yet can't put them back.
		case V4_ALARMS_CLOSED: {
			const closed = (action.alarms || []).filter(a => !!a?.id);
			if (!closed.length) return state;
			const closedIds = new Set(closed.map(a => a.id));
			return state
				.set('v4Alarms', state.get('v4Alarms').filter(a => !closedIds.has(a.id)))
				.set('v4ClosedAlarms', List(closed)
					.concat((state.get('v4ClosedAlarms') || List()).filter(a => !closedIds.has(a.id))))
				.set('v4ClosedLocally', closed.reduce(
					(kept, a) => kept.set(a.id, a), state.get('v4ClosedLocally') || Map()));
		}
		case FETCH_V4_CLOSED_ALARMS:
			return state.set('v4ClosedAlarmsFetching', true);
		case V4_CLOSED_ALARMS_LOADED: {
			const loaded = List(action.alarms.map(d => new Alarm(d)));
			const loadedIds = new Set(loaded.map(a => a.id));
			const pending = (state.get('v4ClosedLocally') || Map()).filter((a, id) => !loadedIds.has(id));
			return state
				.set('v4ClosedAlarms', pending.size ? pending.toList().concat(loaded) : loaded)
				.set('v4ClosedLocally', pending)
				.set('v4ClosedAlarmsFetching', false)
				.set('v4ClosedAlarmsFetched', true);
		}
		case V4_CLOSED_ALARMS_RESET:
			return state
				.set('v4ClosedLocally', Map())
				.set('v4ClosedAlarms', List())
				.set('v4ClosedAlarmsFetching', false)
				.set('v4ClosedAlarmsFetched', false);
		case V4_ALARM_FORWARDING_CHANGED: {
			// Merge the new status onto arc_forward so the ring updates immediately.
			const applyStatus = (alarm) =>
				alarm && alarm.id === action.alarmId
					? alarm.set('arcForward', { ...(alarm.get('arcForward') || {}), status: action.status })
					: alarm;
			let next = state
				.set('v4Alarms', state.get('v4Alarms').map(applyStatus))
				// Remember the live status so a later (possibly stale) refetch can't
				// lose it — covers the alarm only arriving in the list after this
				// event (the fetch/subscription race).
				.setIn(['v4ForwardingStatus', action.alarmId], action.status);
			const detailed = state.get('detailedAlarm');
			if (detailed && detailed.get && detailed.id === action.alarmId) {
				next = next.set('detailedAlarm', applyStatus(detailed));
			}
			return next;
		}
		default:
			return state;
	}
}

export default reducer;
